import { useEffect, useState, cloneElement } from "react";
import { CheckCircle2, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GradientButton } from "@/components/common";
import { useFormModal } from "@/lib/formContext";
import { useI18n } from "@/i18n";
import { saas } from "@/lib/saasApi";

const CONTACT_EMAIL = "realestatepro.be@gmail.com";

const empty = { voornaam: "", achternaam: "", bedrijf: "", email: "", telefoon: "", topic: "", bericht: "", akkoord: false };

export function AccessFormModal() {
  const { open, setOpen, preselect, closeForm } = useFormModal();
  const { t } = useI18n();
  const TOPICS = t("contact.topics");
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setSuccess(false);
      setErrors({});
      setForm({ ...empty, topic: "" });
    }
  }, [open, preselect]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.voornaam.trim()) e.voornaam = t("contact.err.firstName");
    if (!form.achternaam.trim()) e.achternaam = t("contact.err.lastName");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t("contact.err.email");
    if (!form.bericht.trim()) e.bericht = t("contact.err.message");
    if (!form.akkoord) e.akkoord = t("contact.err.agree");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate() || sending) return;
    setSending(true);
    try {
      await saas.contact({
        firstName: form.voornaam, lastName: form.achternaam, email: form.email,
        company: form.bedrijf, phone: form.telefoon, topic: form.topic, message: form.bericht,
      });
      setSuccess(true);
    } catch (err) {
      setErrors({ submit: true });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[92vh] gap-0 overflow-y-auto rounded-3xl border-re-line p-0 [&>button]:hidden sm:max-w-lg" data-testid="access-form-modal">
        <DialogTitle className="sr-only">{t("contact.title")}</DialogTitle>
        <DialogDescription className="sr-only">{t("contact.subtitle")}</DialogDescription>
        {success ? (
          <div className="flex flex-col items-center px-8 py-16 text-center" data-testid="form-success">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-red"><CheckCircle2 className="h-8 w-8 text-white" /></span>
            <h3 className="mt-6 font-heading text-2xl font-bold text-re-ink">{t("contact.successTitle")}</h3>
            <p className="mt-3 max-w-sm text-[15px] text-re-muted">{t("contact.successText")}</p>
            <div className="mt-8"><GradientButton testid="form-success-close" onClick={closeForm}>{t("common.close")}</GradientButton></div>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <div className="relative overflow-hidden rounded-t-3xl bg-re-ink px-8 py-8">
              <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-red opacity-30 blur-3xl" />
              <button type="button" onClick={closeForm} className="absolute right-4 top-4 text-white/60 transition-colors hover:text-white" aria-label={t("common.close")} data-testid="form-close"><X className="h-5 w-5" /></button>
              <h3 className="relative font-heading text-2xl font-black text-white">{t("contact.title")}</h3>
              <p className="relative mt-2 text-sm text-white/60">{t("contact.subtitle")}</p>
            </div>

            <div className="space-y-4 px-8 py-7">
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("contact.firstName")} name="voornaam" error={errors.voornaam}>
                  <input data-testid="field-voornaam" className={inputCls(errors.voornaam)} value={form.voornaam} onChange={(e) => set("voornaam", e.target.value)} />
                </Field>
                <Field label={t("contact.lastName")} name="achternaam" error={errors.achternaam}>
                  <input data-testid="field-achternaam" className={inputCls(errors.achternaam)} value={form.achternaam} onChange={(e) => set("achternaam", e.target.value)} />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label={t("contact.email")} name="email" error={errors.email}>
                  <input type="email" data-testid="field-email" className={inputCls(errors.email)} value={form.email} onChange={(e) => set("email", e.target.value)} />
                </Field>
                <Field label={t("contact.company")} name="bedrijf">
                  <input data-testid="field-bedrijf" className={inputCls(false)} value={form.bedrijf} onChange={(e) => set("bedrijf", e.target.value)} />
                </Field>
              </div>

              <Field label={t("contact.phone")} name="telefoon">
                <input data-testid="field-telefoon" className={inputCls(false)} value={form.telefoon} onChange={(e) => set("telefoon", e.target.value)} />
              </Field>

              <Field label={t("contact.topicLabel")} name="topic" error={errors.topic}>
                <select data-testid="field-topic" className={inputCls(errors.topic)} value={form.topic} onChange={(e) => set("topic", e.target.value)}>
                  <option value="">{t("contact.choose")}</option>
                  {TOPICS.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
                </select>
              </Field>

              <Field label={t("contact.message")} name="bericht" error={errors.bericht}>
                <textarea data-testid="field-bericht" rows={3} className={inputCls(errors.bericht) + " resize-none"} value={form.bericht} onChange={(e) => set("bericht", e.target.value)} />
              </Field>

              <label className="flex cursor-pointer items-start gap-3 pt-1">
                <input type="checkbox" id="akkoord" data-testid="field-akkoord" checked={form.akkoord} onChange={(e) => set("akkoord", e.target.checked)} className="mt-0.5 h-4 w-4 flex-none rounded border-re-line accent-re-red" />
                <span className="text-sm text-re-charcoal">{t("contact.privacyAgree")}</span>
              </label>
              {errors.akkoord && <p className="-mt-2 text-xs text-re-red" data-testid="error-akkoord">{errors.akkoord}</p>}
              {errors.submit && (
                <p className="text-sm text-re-red" data-testid="error-submit">
                  {t("contact.err.submit")}{" "}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold underline" data-testid="error-submit-email">{CONTACT_EMAIL}</a>.
                </p>
              )}

              <GradientButton testid="form-submit" type="submit" className="mt-2 w-full">{sending ? t("contact.sending") : t("contact.submit")}</GradientButton>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function inputCls(error) {
  return `w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-re-ink outline-none transition-colors placeholder:text-re-muted/60 focus:border-re-red ${error ? "border-re-red" : "border-re-line"}`;
}

function Field({ label, error, name, children }) {
  const errId = name ? `${name}-error` : undefined;
  const child = cloneElement(children, { id: name, "aria-invalid": error ? "true" : undefined, "aria-describedby": error ? errId : undefined });
  return (
    <div>
      <label htmlFor={name} className="mb-1.5 block text-xs font-semibold text-re-charcoal">{label}</label>
      {child}
      {error && <p id={errId} className="mt-1 text-xs text-re-red">{error}</p>}
    </div>
  );
}
